import { defineMessages, type IntlShape } from 'react-intl';

import type { PilotId } from '~/eds/src/types/PilotId';
import type { User } from '~/eds/src/types/User';
import type { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import type { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import type { NumberDisplaySettings } from './numberDisplaySettings';
import type { UfFieldValue } from './questionInputRegistry';

import { formatStoredNumber } from './numberDisplaySettings';
import { toUserId } from './userValue';

const messages = defineMessages({
  deactivated: {
    id: 'GetUserName.deactivated',
    defaultMessage: '(Deactivated)',
    description: 'Label indicating a user account has been deactivated',
  },
});

/**
 * The two surfaces that render a multi-value field separate its values
 * differently, and both are kept as they are: the read components use a bullet,
 * while the eds 2.0 panels render one plain `DataValue` string and use a comma.
 */
const READ_COMPONENT_SEPARATOR = ' • ';
export const DISPLAY_TEXT_SEPARATOR = ', ';

export const joinReadValues = (values: string[]) =>
  values.join(READ_COMPONENT_SEPARATOR);

/**
 * Build a file's display name from its stored name and extension. The
 * attachment upload response returns `fileType` with a leading dot ('.pdf')
 * while saved field values carry it without ('pdf'), so the separator is
 * normalized here and skipped when the name already ends with the extension.
 */
export const formatFileName = (
  name: string,
  fileType: string | undefined,
): string => {
  const extension = fileType?.replace(/^\.+/, '') ?? '';
  if (!extension) return name;
  if (name.toLowerCase().endsWith(`.${extension.toLowerCase()}`)) return name;
  return `${name}.${extension}`;
};

/**
 * Plain-text equivalents of each READ component's non-chip render branch —
 * used to build a searchable value string for a UF field. Keep these in sync
 * with the corresponding component in `./read-components/` if its formatting
 * changes.
 */

export const formatTextValues = (values: UfFieldValue[]): string =>
  joinReadValues(values.map((v) => String(v.value ?? '')).filter(Boolean));

export const formatNumberFieldValue = (
  values: UfFieldValue[],
  settings?: NumberDisplaySettings,
): string =>
  joinReadValues(
    values
      .map((v) => formatStoredNumber(v.value, settings))
      .filter((text): text is string => text != null),
  );

export const formatDateFieldValue = (
  values: UfFieldValue[],
  formatDate: ReturnType<typeof useFormatDate>,
): string =>
  joinReadValues(
    values
      .map((v) => v.value as string | undefined)
      .filter(Boolean)
      .map((raw) => formatDate(raw!, 'medium')),
  );

export const formatMonetaryFieldValue = (
  values: UfFieldValue[],
  formatNumber: ReturnType<typeof useFormatNumber>,
): string =>
  joinReadValues(
    values
      .filter(({ amount }) => amount != null)
      .map(({ amount, currency }) => {
        const fractionDigits = String(amount).split('.')[1]?.length ?? 0;
        return formatNumber(
          Number(amount),
          currency ? { currency } : { decimal: fractionDigits },
        );
      }),
  );

export const formatBooleanFieldValue = (
  values: UfFieldValue[],
  intl: IntlShape,
): string => {
  if (!values.length) return '';
  return values[0].value
    ? intl.formatMessage({
        id: 'BooleanReadComponent.yes',
        defaultMessage: 'Yes',
        description: 'Boolean yes display value',
      })
    : intl.formatMessage({
        id: 'BooleanReadComponent.no',
        defaultMessage: 'No',
        description: 'Boolean no display value',
      });
};

export const formatFileFieldValues = (values: UfFieldValue[]): string =>
  joinReadValues(
    values
      .filter((v) => v.name)
      .map((v) => formatFileName(v.name!, v.file_type)),
  );

export const formatUserName = (
  user: Pick<User, 'firstName' | 'lastName' | 'isDeleted'> | undefined,
  fallbackId: PilotId,
  intl: IntlShape,
): string => {
  if (!user) return String(fallbackId);

  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  const baseName = name || String(fallbackId);

  if (!user.isDeleted) return baseName;

  return `${baseName} ${intl.formatMessage(messages.deactivated)}`;
};

export const formatUserFieldValues = (
  values: UfFieldValue[],
  resolveUserName: (id: PilotId) => string,
): string =>
  values
    .map((v) => toUserId(v.value))
    .filter((id): id is PilotId => id != null)
    .map(resolveUserName)
    .join(' • ');
